import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  DollarSign, Download, Loader2, Plus, Search, X, Settings2, Crown, TrendingUp,
  Wallet, ArrowUpDown, Trash2, AlertCircle, ShieldCheck, FileText, Mail, Send,
} from "lucide-react";
import { api } from "../../../lib/api";

const PERIODS = [
  ["all", "All time"], ["today", "Today"], ["yesterday", "Yesterday"],
  ["week", "This week"], ["last_week", "Last week"], ["month", "This month"],
  ["last_month", "Last month"], ["quarter", "Quarter"], ["year", "Year"],
];

const DEPOSIT_METHOD_OPTIONS = [
  ["cashapp",  "Cash App"],
  ["apple_pay","Apple Pay"],
  ["cash",     "Cash"],
  ["zelle",    "Zelle"],
];
const DEPOSIT_METHOD_LABEL = Object.fromEntries(DEPOSIT_METHOD_OPTIONS);
const DEPOSIT_METHOD_STYLE = {
  cashapp:   "bg-lime-500/15  text-lime-200  border-lime-400/40",
  apple_pay: "bg-white/10     text-white     border-white/25",
  cash:      "bg-emerald-500/15 text-emerald-200 border-emerald-400/40",
  zelle:     "bg-violet-500/15 text-violet-200 border-violet-400/40",
};

const COLLECTOR_OPTIONS = [["lance", "Lance"], ["lou", "Lou"]];
const COLLECTOR_LABEL = Object.fromEntries(COLLECTOR_OPTIONS);
const COLLECTOR_STYLE = {
  lance: "bg-cyan/20  text-cyan     border-cyan/40",
  lou:   "bg-amber-500/15 text-amber-200 border-amber-400/40",
};

const STATUS_STYLES = {
  paid:           "bg-lime-500/15 text-lime-200 border-lime-400/40",
  partially_paid: "bg-amber-500/15 text-amber-200 border-amber-400/40",
  pending:        "bg-amber-500/15 text-amber-200 border-amber-400/40",
  unpaid:         "bg-red-500/15 text-red-200 border-red-400/40",
  refunded:       "bg-white/10 text-white/60 border-white/20",
  chargeback:     "bg-red-500/20 text-red-200 border-red-500/60",
  canceled:       "bg-white/5 text-white/40 border-white/10",
  excluded:       "bg-purple-500/15 text-purple-200 border-purple-400/40",
};

/**
 * CRMRevenueSplit — private admin-only ledger for the Lance / Vaughn 60/40
 * revenue split. Reads existing bookings, layered with an overrides layer
 * (`crm_ledger_overrides`) + off-platform manual transactions + payout
 * history so historical rows stay accurate when the default split changes.
 */
export default function CRMRevenueSplit() {
  const [view, setView] = useState("ledger");   // ledger | payouts | settings
  const [period, setPeriod] = useState("month");
  const [q, setQ] = useState("");
  const [data, setData] = useState({ rows: [], totals: {}, settings: {} });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [sortKey, setSortKey] = useState("transaction_date");
  const [sortDir, setSortDir] = useState("desc");
  const [showPayout, setShowPayout] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [payouts, setPayouts] = useState([]);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const [l, p] = await Promise.all([
        api.get("/admin/crm/revenue-split/ledger", { params: { period } }),
        api.get("/admin/crm/revenue-split/payouts"),
      ]);
      setData(l.data || { rows: [], totals: {}, settings: {} });
      setPayouts(p.data || []);
    } catch (err) {
      if (err?.response?.status === 403) setError("Admin permission required to view partner financials.");
      else toast.error("Failed to load ledger");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [period]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let rows = needle
      ? data.rows.filter((r) => [r.customer, r.vehicle, r.transaction_id, r.customer_email]
          .some((f) => (f || "").toLowerCase().includes(needle)))
      : data.rows;
    return [...rows].sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp = typeof av === "number" ? av - bv : String(av).localeCompare(String(bv));
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [data.rows, q, sortKey, sortDir]);

  if (error) return <div className="p-10 text-center text-red-300 text-sm">{error}</div>;

  const T = data.totals || {};
  const S = data.settings || { lance_pct: 60, vaughn_pct: 40 };

  const exportCsv = () => {
    const cols = ["transaction_date","transaction_id","customer","vehicle","service_type",
      "gross","refunds","adjustments","eligible","lance_pct","lance_expected","lance_collected",
      "vaughn_pct","vaughn_expected","vaughn_collected","collected","outstanding",
      "payment_status","payment_method","transaction_status","excluded"];
    const csv = [cols.join(",")].concat(
      filtered.map((r) => cols.map((c) => `"${String(r[c] ?? "").replace(/"/g, '""')}"`).join(","))
    ).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `revenue-split-${period}-${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="crm-revsplit" className="space-y-4">
      {/* Header + view toggle */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="label-caps text-yellow-200 text-[10px] tracking-widest inline-flex items-center gap-1">
            <ShieldCheck size={11} /> Admin only · private
          </div>
          <h3 className="font-serif text-2xl text-white mt-0.5">Revenue Split</h3>
          <div className="text-white/50 text-xs mt-0.5">
            Default split: <span className="text-cyan">Lance {S.lance_pct}% · Vaughn {S.vaughn_pct}%</span> · calc {S.calc_from}
          </div>
        </div>
        <div className="glass rounded-full p-1 flex gap-1">
          {[["ledger","Ledger"], ["payouts","Payouts"], ["statements","Statements"], ["settings","Settings"]].map(([k,l]) => (
            <button key={k} data-testid={`rs-view-${k}`} onClick={() => setView(k)}
              className={`px-4 py-1.5 rounded-full text-xs label-caps transition ${
                view === k ? "bg-cyan text-black font-bold" : "text-white/60 hover:text-white"
              }`}>{l}</button>
          ))}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Stat label="Total revenue" value={T.gross} tone="lime" />
        <Stat label="Split-eligible" value={T.eligible} tone="cyan" />
        <Stat label="Rental deposits held" value={T.held_deposit} tone="amber" />
        <Stat label="Pending (open contracts)" value={T.pending_revenue} tone="amber" />
        <Stat label="Outstanding" value={T.outstanding} tone="red" />
        <Stat label="Refunds + adj." value={(T.refunds || 0) + (T.deductions || 0)} tone="white" />
        <Stat label="Lance earned" value={T.lance_expected} tone="cyan" icon={Crown} />
        <Stat label="Vaughn earned" value={T.vaughn_expected} tone="cyan" icon={Crown} />
        <Stat label="Lance collected" value={T.lance_collected} tone="lime" />
        <Stat label="Vaughn collected" value={T.vaughn_collected} tone="lime" />
        <Stat label="→ Owed Lance" value={T.lance_owed} tone="amber" />
        <Stat label="→ Owed Vaughn" value={T.vaughn_owed} tone="amber" />
      </div>

      {/* Rule of thumb banner */}
      <div className="rounded-xl border border-cyan/25 bg-cyan/[0.04] px-3 py-2 text-[11px] text-white/70 leading-relaxed flex items-start gap-2">
        <ShieldCheck size={14} className="text-cyan mt-0.5 shrink-0" />
        <div>
          <span className="text-cyan font-bold">Deposit rules:</span> Chauffeur deposits count as revenue the moment they're paid.
          Self-drive rental deposits stay <span className="text-amber-300">held</span> and only flip into split-eligible revenue when the contract is closed (status → completed / checked-in / returned).
        </div>
      </div>

      {view === "ledger" && (
        <>
          {/* Filters */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex-1 min-w-[220px] relative">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input value={q} onChange={(e) => setQ(e.target.value)}
                data-testid="rs-search"
                placeholder="Search customer, vehicle, txn id…"
                className="w-full pl-9 pr-3 py-2 bg-black/40 border border-white/10 focus:border-cyan/60 rounded-full text-sm text-white outline-none" />
            </div>
            <select value={period} onChange={(e) => setPeriod(e.target.value)}
              data-testid="rs-period"
              className="bg-black/50 border border-white/15 rounded-full px-3 py-2 text-xs text-white outline-none">
              {PERIODS.map(([k,l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <button onClick={() => setShowManual(true)} data-testid="rs-manual-btn"
              className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-white/15 text-white/70 text-xs hover:bg-white/5 transition">
              <Plus size={12} /> Manual txn
            </button>
            <button onClick={exportCsv} data-testid="rs-export"
              className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-cyan text-black text-xs font-bold label-caps transition">
              <Download size={12} /> Export CSV
            </button>
          </div>

          {/* Spreadsheet */}
          <div className="rounded-2xl border border-white/10 bg-black/25 overflow-hidden">
            {loading ? (
              <div className="p-10 text-center text-white/50 text-sm inline-flex items-center gap-2 justify-center w-full">
                <Loader2 size={14} className="animate-spin text-cyan" /> Crunching numbers…
              </div>
            ) : (
              <div className="overflow-x-auto max-h-[70vh]">
                <table className="w-full text-xs tabular-nums">
                  <thead className="sticky top-0 bg-ink-900 z-10">
                    <tr className="text-white/50 label-caps text-[9px] border-b border-white/10">
                      {[["transaction_date","Booked"],["rental_start","Reserved for"],
                        ["customer","Customer"],["vehicle","Vehicle"],
                        ["service_type","Svc"],["gross","Gross"],["held_deposit","Held"],
                        ["eligible","Eligible"],["lance_expected","Lance"],
                        ["vaughn_expected","Vaughn"],["collected","Collected"],
                        ["outstanding","Balance"],["payment_status","Status"]].map(([k,l]) => (
                        <th key={k} className="text-left px-2 py-2 whitespace-nowrap cursor-pointer hover:text-white"
                          onClick={() => { setSortKey(k); setSortDir((d) => sortKey === k && d === "desc" ? "asc" : "desc"); }}>
                          {l} {sortKey === k ? (sortDir === "asc" ? "↑" : "↓") : ""}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {filtered.map((r) => {
                      const stKey = r.excluded ? "excluded" : (r.payment_status || "pending");
                      const style = STATUS_STYLES[stKey] || STATUS_STYLES.pending;
                      return (
                        <tr key={r.transaction_id} data-testid={`rs-row-${r.transaction_id}`}
                          onClick={() => setSelected(r)}
                          className="hover:bg-cyan/[0.05] cursor-pointer">
                          <td className="px-2 py-2 text-white/70 whitespace-nowrap">
                            {r.transaction_date ? new Date(r.transaction_date).toLocaleDateString() : "—"}
                          </td>
                          <td className="px-2 py-2 whitespace-nowrap"
                            data-testid={`rs-reserved-${r.transaction_id}`}>
                            {r.rental_start ? (
                              <div className="flex flex-col leading-tight">
                                <span className="text-white text-[11px]">
                                  {new Date(r.rental_start).toLocaleDateString(undefined,
                                    { month: "short", day: "numeric", year: "numeric" })}
                                </span>
                                <span className="text-white/50 text-[9px]">
                                  {new Date(r.rental_start).toLocaleTimeString(undefined,
                                    { hour: "numeric", minute: "2-digit" })}
                                  {r.rental_end && r.service_type === "rental"
                                    ? ` → ${new Date(r.rental_end).toLocaleDateString(undefined, { month: "short", day: "numeric" })}`
                                    : ""}
                                </span>
                              </div>
                            ) : <span className="text-white/30">—</span>}
                          </td>
                          <td className="px-2 py-2 text-white truncate max-w-[140px]">{r.customer || "—"}</td>
                          <td className="px-2 py-2 text-white/60 truncate max-w-[140px]">{r.vehicle || "—"}</td>
                          <td className="px-2 py-2 text-white/50 label-caps text-[9px]">
                            <span className="inline-flex items-center gap-1">
                              {r.service_type === "rental" ? "rental" : "chauffeur"}
                              {r.service_type === "rental" && r.contract_closed === false && (
                                <span data-testid={`rs-open-${r.transaction_id}`}
                                  className="text-[8px] px-1 py-0.5 rounded bg-amber-500/20 text-amber-200 border border-amber-400/30">open</span>
                              )}
                            </span>
                          </td>
                          <td className="px-2 py-2 text-lime-300">${r.gross.toFixed(0)}</td>
                          <td className="px-2 py-2"
                            data-testid={`rs-held-${r.transaction_id}`}>
                            <div className="flex items-center gap-1 flex-wrap">
                              <span className="text-amber-200 whitespace-nowrap">
                                {(r.held_deposit > 0 || r.deposit_paid_amount > 0)
                                  ? `$${(r.held_deposit || r.deposit_paid_amount).toFixed(0)}`
                                  : "—"}
                              </span>
                              {r.deposit_method && (
                                <span className={`label-caps text-[8px] px-1 py-0.5 rounded border ${DEPOSIT_METHOD_STYLE[r.deposit_method] || "bg-white/5 border-white/15 text-white/60"}`}
                                  title="Deposit paid via">
                                  {DEPOSIT_METHOD_LABEL[r.deposit_method] || r.deposit_method}
                                </span>
                              )}
                              {r.deposit_collected_by && (
                                <span className={`label-caps text-[8px] px-1 py-0.5 rounded border ${COLLECTOR_STYLE[r.deposit_collected_by] || "bg-white/5 border-white/15 text-white/60"}`}
                                  title="Collected by">
                                  {COLLECTOR_LABEL[r.deposit_collected_by] || r.deposit_collected_by}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-2 py-2 text-cyan"
                            data-testid={`rs-eligible-${r.transaction_id}`}>
                            {r.pending_revenue > 0
                              ? <span className="text-white/40 italic">pending</span>
                              : `$${r.eligible.toFixed(0)}`}
                          </td>
                          <td className="px-2 py-2 text-cyan">${r.lance_expected.toFixed(0)}<span className="text-white/30 text-[10px] ml-1">({r.lance_pct}%)</span></td>
                          <td className="px-2 py-2 text-cyan">${r.vaughn_expected.toFixed(0)}<span className="text-white/30 text-[10px] ml-1">({r.vaughn_pct}%)</span></td>
                          <td className="px-2 py-2 text-lime-300">${r.collected.toFixed(0)}</td>
                          <td className="px-2 py-2 text-amber-200">{r.outstanding > 0 ? `$${r.outstanding.toFixed(0)}` : "—"}</td>
                          <td className="px-2 py-2">
                            <span className={`label-caps text-[9px] px-1.5 py-0.5 rounded-full border ${style}`}>
                              {stKey.replace(/_/g, " ")}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                    {filtered.length === 0 && (
                      <tr><td colSpan="13" className="p-10 text-center text-white/40 italic">No transactions match.</td></tr>
                    )}
                  </tbody>
                  {filtered.length > 0 && (
                    <tfoot className="border-t border-cyan/30 bg-cyan/[0.04] font-bold">
                      <tr className="text-white">
                        <td colSpan="5" className="px-2 py-2 label-caps text-[10px] text-cyan">Totals · {filtered.length} rows</td>
                        <td className="px-2 py-2 text-lime-300">${filtered.reduce((s,r)=>s+r.gross,0).toFixed(0)}</td>
                        <td className="px-2 py-2 text-amber-200">${filtered.reduce((s,r)=>s+(r.held_deposit||0),0).toFixed(0)}</td>
                        <td className="px-2 py-2 text-cyan">${filtered.reduce((s,r)=>s+r.eligible,0).toFixed(0)}</td>
                        <td className="px-2 py-2 text-cyan">${filtered.reduce((s,r)=>s+r.lance_expected,0).toFixed(0)}</td>
                        <td className="px-2 py-2 text-cyan">${filtered.reduce((s,r)=>s+r.vaughn_expected,0).toFixed(0)}</td>
                        <td className="px-2 py-2 text-lime-300">${filtered.reduce((s,r)=>s+r.collected,0).toFixed(0)}</td>
                        <td className="px-2 py-2 text-amber-200">${filtered.reduce((s,r)=>s+r.outstanding,0).toFixed(0)}</td>
                        <td />
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {view === "payouts" && (
        <PayoutsView payouts={payouts} onNew={() => setShowPayout(true)}
          onReversed={load} totals={T} />
      )}

      {view === "settings" && <SettingsView settings={S} onSaved={(next) => { setData({ ...data, settings: next }); toast.success("Split defaults saved (historical rows unchanged)"); }} />}

      {view === "statements" && <StatementsView />}

      {selected && <DetailDrawer row={selected} onClose={() => setSelected(null)} onChanged={load} />}
      {showPayout && <PayoutForm totals={T} onClose={() => setShowPayout(false)} onSaved={() => { setShowPayout(false); load(); }} />}
      {showManual && <ManualTxnForm onClose={() => setShowManual(false)} onSaved={() => { setShowManual(false); load(); }} />}
    </div>
  );
}

function Stat({ label, value, tone = "cyan", icon: Icon }) {
  const tones = { cyan: "text-cyan border-cyan/25", lime: "text-lime-300 border-lime-400/25",
    red: "text-red-300 border-red-400/25", amber: "text-amber-300 border-amber-400/25",
    white: "text-white/70 border-white/15" };
  return (
    <div className={`rounded-xl border bg-black/30 p-3 ${tones[tone]}`}>
      <div className="flex items-center justify-between">
        <div className="label-caps text-[9px] text-white/60 tracking-widest truncate">{label}</div>
        {Icon && <Icon size={12} className="opacity-60" />}
      </div>
      <div className="font-serif text-xl mt-0.5 tabular-nums">
        ${Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
      </div>
    </div>
  );
}

function DetailDrawer({ row, onClose, onChanged }) {
  const [tab, setTab] = useState("summary");
  const [lp, setLp] = useState(row.lance_pct);
  const [vp, setVp] = useState(row.vaughn_pct);
  const [rsn, setRsn] = useState("");
  const [saving, setSaving] = useState(false);
  const [ded, setDed] = useState({ kind: "processing_fee", amount: "", description: "" });
  const [noteDraft, setNoteDraft] = useState("");

  const patch = async (body) => {
    setSaving(true);
    try {
      await api.patch(`/admin/crm/revenue-split/ledger/${row.transaction_id}`, body);
      toast.success("Saved"); onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md p-4 flex items-start justify-center overflow-y-auto" onClick={onClose}>
      <div className="bg-ink-900 border border-cyan/25 rounded-2xl max-w-2xl w-full my-8 p-5 space-y-4"
        data-testid="rs-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <div className="label-caps text-cyan text-[10px]">Transaction · {row.transaction_id?.slice(-8)}</div>
            <h3 className="font-serif text-xl mt-0.5">{row.customer}</h3>
            <div className="text-white/50 text-xs">{row.vehicle} · {row.service_type}</div>
            {row.rental_start && (
              <div className="mt-2 inline-flex items-center gap-2 rounded-lg border border-cyan/30 bg-cyan/[0.06] px-2 py-1 text-[11px] text-white/80"
                data-testid="rs-drawer-reservation">
                <span className="label-caps text-[9px] text-cyan">Reserved for</span>
                <span>
                  {new Date(row.rental_start).toLocaleString(undefined,
                    { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}
                  {row.rental_end && row.service_type === "rental"
                    ? ` → ${new Date(row.rental_end).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}`
                    : ""}
                </span>
              </div>
            )}
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/5"><X size={16} /></button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Stat label="Gross" value={row.gross} tone="lime" />
          <Stat label="Eligible" value={row.eligible} tone="cyan" />
          <Stat label="Collected" value={row.collected} tone="lime" />
          <Stat label="Outstanding" value={row.outstanding} tone="amber" />
          <Stat label={`Lance · ${row.lance_pct}%`} value={row.lance_expected} tone="cyan" />
          <Stat label="Lance collected" value={row.lance_collected} tone="lime" />
          <Stat label={`Vaughn · ${row.vaughn_pct}%`} value={row.vaughn_expected} tone="cyan" />
          <Stat label="Vaughn collected" value={row.vaughn_collected} tone="lime" />
        </div>

        {/* Deposit collection details */}
        <div className="rounded-xl border border-white/10 bg-black/30 p-3 space-y-2">
          <div className="label-caps text-[10px] text-cyan tracking-widest">Deposit collection</div>
          <div className="text-white/50 text-[11px]">
            Deposits can only be collected via <span className="text-white/80">Cash App, Apple Pay, Cash, or Zelle.</span>
          </div>
          {/* … existing amount + paid via + collected by … */}
          <div className="flex gap-2 flex-wrap">
            <label className="w-full sm:w-32">
              <div className="label-caps text-[9px] text-white/50 mb-1">Amount</div>
              <div className="relative">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-white/40 text-sm">$</span>
                <input
                  type="number"
                  data-testid="rs-deposit-amount"
                  defaultValue={row.deposit_paid_amount || ""}
                  placeholder="0"
                  min="0"
                  step="1"
                  onBlur={(e) => {
                    const raw = e.target.value.trim();
                    const nextVal = raw === "" ? 0 : Number(raw);
                    if (Number.isNaN(nextVal)) return;
                    if (Number(row.deposit_paid_amount || 0) === nextVal) return;
                    patch({ deposit_paid_amount: nextVal });
                  }}
                  onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                  className="w-full bg-black/50 border border-white/15 rounded-lg pl-6 pr-2 py-2 text-sm text-white outline-none focus:border-cyan/50"
                />
              </div>
            </label>
            <label className="flex-1 min-w-[150px]">
              <div className="label-caps text-[9px] text-white/50 mb-1">Paid via</div>
              <select
                data-testid="rs-deposit-method"
                defaultValue={row.deposit_method || ""}
                onChange={(e) => patch({ deposit_method: e.target.value })}
                className="w-full bg-black/50 border border-white/15 rounded-lg px-3 py-2 text-sm text-white outline-none">
                <option value="">— select —</option>
                {DEPOSIT_METHOD_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </label>
            <label className="flex-1 min-w-[150px]">
              <div className="label-caps text-[9px] text-white/50 mb-1">Collected by</div>
              <select
                data-testid="rs-deposit-collector"
                defaultValue={row.deposit_collected_by || ""}
                onChange={(e) => patch({ deposit_collected_by: e.target.value })}
                className="w-full bg-black/50 border border-white/15 rounded-lg px-3 py-2 text-sm text-white outline-none">
                <option value="">— select —</option>
                {COLLECTOR_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </label>
          </div>
        </div>

        {/* Deposit refund — only show when a deposit was actually collected. */}
        {(row.deposit_paid_amount > 0 || row.deposit_refunded_amount > 0) && (
          <DepositRefundPanel row={row} patch={patch} />
        )}

        {/* Gross amount — manager override. Writes straight onto the booking's
            amount + locks the price so no auto re-price clobbers it. */}
        <div className="rounded-xl border border-cyan/25 bg-cyan/[0.05] p-3 space-y-2">
          <div className="flex items-center justify-between">
            <div className="label-caps text-[10px] text-cyan tracking-widest">Gross amount</div>
            <div className="text-[10px] text-white/40">writes to booking · locks price</div>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-sm">$</span>
              <input
                type="number" min="0" step="0.01"
                data-testid="rs-gross-input"
                defaultValue={row.gross || 0}
                onBlur={(e) => {
                  const raw = e.target.value.trim();
                  const next = raw === "" ? 0 : Number(raw);
                  if (Number.isNaN(next) || Number(row.gross || 0) === next) return;
                  patch({ gross: next });
                }}
                onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                className="w-full bg-black/50 border border-cyan/40 rounded-lg pl-7 pr-3 py-2 text-lg font-semibold text-white outline-none focus:border-cyan"
              />
            </div>
            <span className="text-[10px] text-white/40">current · ${Number(row.gross || 0).toFixed(2)}</span>
          </div>
        </div>

        {/* Custom override */}
        <div className="rounded-xl border border-white/10 bg-black/30 p-3 space-y-2">
          <div className="label-caps text-[10px] text-cyan tracking-widest">Split override</div>
          <div className="flex gap-2 items-center flex-wrap">
            <input type="number" value={lp} onChange={(e) => { const v = Number(e.target.value); setLp(v); setVp(100 - v); }}
              data-testid="rs-lp" min="0" max="100"
              className="w-20 bg-black/50 border border-white/15 rounded-lg px-2 py-1.5 text-sm text-white outline-none" />
            <span className="text-white/50 text-xs">Lance %</span>
            <input type="number" value={vp} disabled
              className="w-20 bg-black/50 border border-white/15 rounded-lg px-2 py-1.5 text-sm text-white/60 outline-none" />
            <span className="text-white/50 text-xs">Vaughn %</span>
            <input value={rsn} onChange={(e) => setRsn(e.target.value)} placeholder="Reason (required)"
              className="flex-1 min-w-[140px] bg-black/50 border border-white/15 rounded-lg px-3 py-1.5 text-sm text-white outline-none" />
            <button disabled={!rsn.trim() || saving} data-testid="rs-override-save"
              onClick={() => patch({ custom_lance_pct: lp, custom_vaughn_pct: vp, override_reason: rsn })}
              className="px-3 py-1.5 rounded-lg bg-cyan text-black text-xs font-bold label-caps disabled:opacity-40">
              Apply
            </button>
          </div>
          <button onClick={() => {
              const reason = prompt(row.excluded ? "Reason for un-excluding?" : "Why exclude this transaction?");
              if (reason !== null) patch({ excluded: !row.excluded, exclusion_reason: reason });
            }}
            data-testid="rs-exclude-btn"
            className={`w-full mt-1 text-xs label-caps py-1.5 rounded-lg border transition ${
              row.excluded
                ? "border-purple-400/50 text-purple-200 bg-purple-500/10 hover:bg-purple-500/20"
                : "border-white/15 text-white/60 hover:text-white hover:border-white/40"
            }`}>
            {row.excluded ? "✕ Excluded from split — click to include" : "Exclude from partner split"}
          </button>
        </div>

        {/* Deductions */}
        <div className="rounded-xl border border-white/10 bg-black/30 p-3 space-y-2">
          <div className="label-caps text-[10px] text-cyan tracking-widest">Add deduction</div>
          <div className="flex gap-2">
            <select value={ded.kind} onChange={(e) => setDed({ ...ded, kind: e.target.value })}
              className="bg-black/50 border border-white/15 rounded-lg px-2 py-1.5 text-xs text-white outline-none">
              {["processing_fee","refund","discount","chargeback","vehicle_expense","chauffeur_expense","referral_commission","other"].map((k) =>
                <option key={k} value={k}>{k.replace(/_/g," ")}</option>)}
            </select>
            <input type="number" placeholder="$ amount" value={ded.amount}
              onChange={(e) => setDed({ ...ded, amount: e.target.value })}
              className="w-28 bg-black/50 border border-white/15 rounded-lg px-2 py-1.5 text-sm text-white outline-none" />
            <input placeholder="description" value={ded.description}
              onChange={(e) => setDed({ ...ded, description: e.target.value })}
              className="flex-1 bg-black/50 border border-white/15 rounded-lg px-3 py-1.5 text-sm text-white outline-none" />
            <button data-testid="rs-add-deduction" disabled={!ded.amount || saving}
              onClick={() => patch({ add_deduction: { ...ded, amount: Number(ded.amount) } }).then(() => setDed({ kind: "processing_fee", amount: "", description: "" }))}
              className="px-3 py-1.5 rounded-lg bg-amber-500 text-black text-xs font-bold label-caps disabled:opacity-40">
              Add
            </button>
          </div>
          {(row.deductions || []).length > 0 && (
            <div className="text-xs text-white/70 space-y-1 mt-1">
              {row.deductions.map((d, i) => (
                <div key={i} className="flex justify-between border-l-2 border-amber-400/40 pl-2">
                  <span>{d.kind?.replace(/_/g," ")} · {d.description}</span>
                  <span className="text-amber-200">-${Number(d.amount).toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Notes */}
        <div className="rounded-xl border border-white/10 bg-black/30 p-3">
          <div className="label-caps text-[10px] text-cyan tracking-widest mb-2">Notes &amp; audit</div>
          <div className="flex gap-2 mb-2">
            <input value={noteDraft} onChange={(e) => setNoteDraft(e.target.value)}
              placeholder="Add a note…"
              className="flex-1 bg-black/50 border border-white/15 rounded-lg px-3 py-1.5 text-sm text-white outline-none" />
            <button onClick={() => noteDraft.trim() && patch({ add_note: noteDraft.trim() }).then(() => setNoteDraft(""))}
              className="px-3 py-1.5 rounded-lg bg-cyan text-black text-xs font-bold label-caps">Add</button>
          </div>
          {[...(row.notes || []), ...(row.audit || [])].slice(0, 12).map((n, i) => (
            <div key={i} className="text-[11px] text-white/60 border-l border-white/10 pl-2 mb-1">
              {n.text || (n.changes && Object.entries(n.changes).map(([k,v]) => `${k}=${v}`).join(", "))}
              <div className="text-white/30 text-[10px]">{new Date(n.at).toLocaleString()} · {n.by}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PayoutsView({ payouts, onNew, onReversed, totals }) {
  const reverse = async (id) => {
    if (!window.confirm("Reverse this payout? (Audit trail will keep the record)")) return;
    await api.delete(`/admin/crm/revenue-split/payouts/${id}`);
    toast.success("Payout reversed"); onReversed();
  };
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-white/60 text-xs">
          Owed Lance: <span className="text-amber-300 font-bold">${(totals.lance_owed || 0).toFixed(0)}</span> ·
          Owed Vaughn: <span className="text-amber-300 font-bold ml-1">${(totals.vaughn_owed || 0).toFixed(0)}</span>
        </div>
        <button data-testid="rs-payout-new" onClick={onNew}
          className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-cyan text-black text-xs font-bold label-caps">
          <Plus size={12} /> Record payout
        </button>
      </div>
      <div className="rounded-2xl border border-white/10 bg-black/25 overflow-hidden">
        <table className="w-full text-xs tabular-nums">
          <thead className="bg-ink-900">
            <tr className="text-white/50 label-caps text-[9px] border-b border-white/10">
              <th className="text-left px-3 py-2">Date</th>
              <th className="text-left px-3 py-2">Partner</th>
              <th className="text-right px-3 py-2">Amount</th>
              <th className="text-left px-3 py-2">Method</th>
              <th className="text-left px-3 py-2">Ref</th>
              <th className="text-left px-3 py-2">Period</th>
              <th className="text-left px-3 py-2">By</th>
              <th></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {payouts.map((p) => (
              <tr key={p.id} className="hover:bg-white/5">
                <td className="px-3 py-2 text-white/70">{new Date(p.date).toLocaleDateString()}</td>
                <td className="px-3 py-2 text-white capitalize">{p.partner}</td>
                <td className="px-3 py-2 text-lime-300 text-right">${p.amount.toFixed(2)}</td>
                <td className="px-3 py-2 text-white/60">{p.payment_method}</td>
                <td className="px-3 py-2 text-white/50 truncate max-w-[140px]">{p.reference || "—"}</td>
                <td className="px-3 py-2 text-white/50">{p.period_covered || "—"}</td>
                <td className="px-3 py-2 text-white/40 text-[10px]">{p.processed_by}</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => reverse(p.id)} className="text-white/40 hover:text-red-300"><Trash2 size={12} /></button>
                </td>
              </tr>
            ))}
            {payouts.length === 0 && (
              <tr><td colSpan="8" className="p-8 text-center text-white/40 italic">No payouts recorded yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SettingsView({ settings, onSaved }) {
  const [lp, setLp] = useState(settings.lance_pct);
  const [vp, setVp] = useState(settings.vaughn_pct);
  const [cf, setCf] = useState(settings.calc_from || "GROSS");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (Math.round(lp + vp) !== 100) { toast.error("Percentages must total 100"); return; }
    setSaving(true);
    try {
      const r = await api.patch("/admin/crm/revenue-split/settings", { lance_pct: lp, vaughn_pct: vp, calc_from: cf });
      onSaved(r.data);
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="rounded-2xl border border-cyan/25 bg-black/25 p-5 max-w-xl space-y-4">
      <div>
        <div className="label-caps text-[10px] text-cyan">Default split (applies to future transactions only)</div>
        <p className="text-white/60 text-xs mt-1">Historical rows keep the percentages they were recorded with — changing this here does NOT rewrite the past.</p>
      </div>
      <div className="flex items-center gap-3">
        <label className="flex-1">
          <div className="label-caps text-[9px] text-white/50 mb-1">Lance %</div>
          <input type="number" value={lp} onChange={(e) => { const v = Number(e.target.value); setLp(v); setVp(100 - v); }}
            data-testid="rs-settings-lp" min="0" max="100"
            className="w-full bg-black/50 border border-white/15 rounded-lg px-3 py-2 text-sm text-white outline-none" />
        </label>
        <label className="flex-1">
          <div className="label-caps text-[9px] text-white/50 mb-1">Vaughn %</div>
          <input type="number" value={vp} disabled
            className="w-full bg-black/50 border border-white/15 rounded-lg px-3 py-2 text-sm text-white/60 outline-none" />
        </label>
      </div>
      <label>
        <div className="label-caps text-[9px] text-white/50 mb-1">Calculate from</div>
        <select value={cf} onChange={(e) => setCf(e.target.value)}
          data-testid="rs-settings-cf"
          className="w-full bg-black/50 border border-white/15 rounded-lg px-3 py-2 text-sm text-white outline-none">
          <option value="GROSS">Gross revenue</option>
          <option value="NET">Net split-eligible (after deductions)</option>
        </select>
      </label>
      <button onClick={save} disabled={saving} data-testid="rs-settings-save"
        className="w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-cyan text-black font-bold text-xs label-caps disabled:opacity-50">
        {saving ? <Loader2 size={13} className="animate-spin" /> : <ShieldCheck size={13} />} Save default split
      </button>
    </div>
  );
}

function PayoutForm({ totals, onClose, onSaved }) {
  const [form, setForm] = useState({ partner: "lance", amount: "", payment_method: "Zelle", reference: "", period_covered: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/crm/revenue-split/payouts", { ...form, amount: Number(form.amount) });
      toast.success("Payout recorded"); onSaved();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md p-4 flex items-center justify-center" onClick={onClose}>
      <form onSubmit={submit} className="bg-ink-900 border border-cyan/25 rounded-2xl max-w-md w-full p-5 space-y-3"
        data-testid="rs-payout-form" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start">
          <h3 className="font-serif text-xl">Record payout</h3>
          <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-white/5"><X size={16} /></button>
        </div>
        <div className="text-white/50 text-xs">
          Currently owed: Lance <span className="text-amber-300">${(totals.lance_owed || 0).toFixed(0)}</span> ·
          Vaughn <span className="text-amber-300 ml-1">${(totals.vaughn_owed || 0).toFixed(0)}</span>
        </div>
        <select value={form.partner} onChange={(e) => setForm({ ...form, partner: e.target.value })}
          data-testid="rs-payout-partner"
          className="w-full bg-black/50 border border-white/15 rounded-lg px-3 py-2 text-sm text-white outline-none">
          <option value="lance">Lance</option>
          <option value="vaughn">Vaughn</option>
        </select>
        <input type="number" step="0.01" required placeholder="Amount"
          value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
          data-testid="rs-payout-amount"
          className="w-full bg-black/50 border border-white/15 rounded-lg px-3 py-2 text-sm text-white outline-none" />
        <select value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })}
          className="w-full bg-black/50 border border-white/15 rounded-lg px-3 py-2 text-sm text-white outline-none">
          {["Zelle","Cash","Wire","ACH","Check","Cash App","Venmo","Other"].map((m) => <option key={m}>{m}</option>)}
        </select>
        <input placeholder="Reference / confirmation #" value={form.reference}
          onChange={(e) => setForm({ ...form, reference: e.target.value })}
          className="w-full bg-black/50 border border-white/15 rounded-lg px-3 py-2 text-sm text-white outline-none" />
        <input placeholder="Period covered (e.g. Aug 1-15 2026)" value={form.period_covered}
          onChange={(e) => setForm({ ...form, period_covered: e.target.value })}
          className="w-full bg-black/50 border border-white/15 rounded-lg px-3 py-2 text-sm text-white outline-none" />
        <textarea placeholder="Notes (optional)" value={form.notes} rows={2}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          className="w-full bg-black/50 border border-white/15 rounded-lg px-3 py-2 text-sm text-white outline-none resize-none" />
        <button type="submit" disabled={saving} data-testid="rs-payout-submit"
          className="w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-cyan text-black font-bold text-xs label-caps disabled:opacity-50">
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Wallet size={13} />} Record payout
        </button>
      </form>
    </div>
  );
}

function ManualTxnForm({ onClose, onSaved }) {
  const [form, setForm] = useState({ customer: "", amount: "", service: "rental", vehicle: "",
    payment_method: "Cash", reference: "", notes: "", date: new Date().toISOString().slice(0,10) });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.post("/admin/crm/revenue-split/manual-transaction", { ...form, amount: Number(form.amount) });
      toast.success("Transaction added"); onSaved();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md p-4 flex items-center justify-center overflow-y-auto" onClick={onClose}>
      <form onSubmit={submit} className="bg-ink-900 border border-cyan/25 rounded-2xl max-w-md w-full p-5 space-y-3 my-auto"
        data-testid="rs-manual-form" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start">
          <h3 className="font-serif text-xl">Manual transaction</h3>
          <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-white/5"><X size={16} /></button>
        </div>
        <p className="text-white/50 text-xs">For payments collected outside the website (cash, Zelle, wire, etc.).</p>
        <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
          className="w-full bg-black/50 border border-white/15 rounded-lg px-3 py-2 text-sm text-white outline-none" />
        <input required placeholder="Customer name / description" value={form.customer}
          onChange={(e) => setForm({ ...form, customer: e.target.value })}
          data-testid="rs-manual-customer"
          className="w-full bg-black/50 border border-white/15 rounded-lg px-3 py-2 text-sm text-white outline-none" />
        <input type="number" step="0.01" required placeholder="Amount ($)"
          value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
          data-testid="rs-manual-amount"
          className="w-full bg-black/50 border border-white/15 rounded-lg px-3 py-2 text-sm text-white outline-none" />
        <div className="grid grid-cols-2 gap-2">
          <select value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })}
            className="bg-black/50 border border-white/15 rounded-lg px-3 py-2 text-sm text-white outline-none">
            {["rental","ride","event","content","other"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })}
            className="bg-black/50 border border-white/15 rounded-lg px-3 py-2 text-sm text-white outline-none">
            {["Cash","Zelle","Wire","ACH","Check","External Card","Other"].map((m) => <option key={m}>{m}</option>)}
          </select>
        </div>
        <input placeholder="Vehicle (optional)" value={form.vehicle}
          onChange={(e) => setForm({ ...form, vehicle: e.target.value })}
          className="w-full bg-black/50 border border-white/15 rounded-lg px-3 py-2 text-sm text-white outline-none" />
        <input placeholder="Reference / confirmation" value={form.reference}
          onChange={(e) => setForm({ ...form, reference: e.target.value })}
          className="w-full bg-black/50 border border-white/15 rounded-lg px-3 py-2 text-sm text-white outline-none" />
        <textarea placeholder="Notes" rows={2} value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          className="w-full bg-black/50 border border-white/15 rounded-lg px-3 py-2 text-sm text-white outline-none resize-none" />
        <button type="submit" disabled={saving} data-testid="rs-manual-submit"
          className="w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-cyan text-black font-bold text-xs label-caps disabled:opacity-50">
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Add transaction
        </button>
      </form>
    </div>
  );
}

// ── Monthly Partner Statements ──────────────────────────────────────
function StatementsView() {
  const [month, setMonth] = useState(() => {
    const d = new Date();
    d.setDate(1); d.setMonth(d.getMonth() - 1);   // default = previous month
    return d.toISOString().slice(0, 7);
  });
  const [previews, setPreviews] = useState({ lance: null, vaughn: null });
  const [recipients, setRecipients] = useState({ lance_email: "", vaughn_email: "" });
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(null);   // partner key currently sending
  const [testTo, setTestTo] = useState("");

  const backend = process.env.REACT_APP_BACKEND_URL || "";

  const loadPreviews = async () => {
    setLoading(true);
    try {
      const [l, v, r] = await Promise.all([
        api.get("/admin/crm/revenue-split/statement/preview", { params: { partner: "lance",  month } }),
        api.get("/admin/crm/revenue-split/statement/preview", { params: { partner: "vaughn", month } }),
        api.get("/admin/crm/revenue-split/statement-recipients"),
      ]);
      setPreviews({ lance: l.data, vaughn: v.data });
      setRecipients({
        lance_email:  r.data?.lance_email  || "",
        vaughn_email: r.data?.vaughn_email || "",
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load statement previews");
    } finally { setLoading(false); }
  };
  useEffect(() => { loadPreviews(); /* eslint-disable-next-line */ }, [month]);

  const saveRecipients = async () => {
    try {
      await api.post("/admin/crm/revenue-split/statement-recipients", recipients);
      toast.success("Recipient emails saved");
    } catch { toast.error("Save failed"); }
  };

  const downloadPdf = (partner) => {
    const url = `${backend}/api/admin/crm/revenue-split/statement.pdf?partner=${partner}&month=${month}`;
    window.open(url, "_blank");
  };

  const sendStatements = async (partners, useTestTo) => {
    setSending(partners.join(","));
    try {
      const r = await api.post("/admin/crm/revenue-split/email-statements", {
        month, partners, test_to: useTestTo ? testTo : undefined,
      });
      const failed = (r.data?.results || []).filter((x) => !x.delivered);
      if (failed.length === 0) {
        toast.success(`Sent ${r.data?.results?.length || 0} statement(s)`);
      } else {
        toast.error(`Some deliveries failed: ${failed.map((x) => `${x.partner} (${x.reason || "?"})`).join(", ")}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    } finally { setSending(null); }
  };

  return (
    <div className="space-y-4" data-testid="rs-statements-view">
      <div className="rounded-2xl border border-cyan/20 bg-black/25 p-4 space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <div>
            <div className="label-caps text-[10px] text-cyan tracking-widest">Statement month</div>
            <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
              data-testid="rs-statement-month"
              className="mt-1 bg-black/50 border border-white/15 rounded-lg px-3 py-2 text-sm text-white outline-none" />
          </div>
          <div className="text-white/50 text-[11px] flex-1 max-w-md">
            Auto-runs on the <span className="text-cyan">1st of every month at 9 AM ET</span> for the previous month.
            You can also send manually or download the PDF here.
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {["lance", "vaughn"].map((p) => {
          const snap = previews[p];
          const label = p === "lance" ? "Lance" : "Vaughn";
          const email = recipients[`${p}_email`];
          return (
            <div key={p} data-testid={`rs-statement-card-${p}`}
              className="rounded-2xl border border-white/10 bg-black/25 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="label-caps text-[10px] text-cyan">{label} · {snap?.month_label || month}</div>
                  <h4 className="font-serif text-xl text-white mt-0.5">Partner Statement</h4>
                </div>
                <Crown size={18} className="text-cyan/60" />
              </div>

              {loading || !snap ? (
                <div className="text-white/40 text-xs italic flex items-center gap-2">
                  <Loader2 size={12} className="animate-spin" /> Loading…
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <StatRow label="Gross" value={snap.gross} />
                  <StatRow label="Split-eligible" value={snap.eligible} />
                  <StatRow label={`${label} expected`} value={snap.expected} tone="cyan" />
                  <StatRow label={`${label} collected`} value={snap.collected} tone="lime" />
                  <StatRow label="Already paid out" value={snap.paid_out} />
                  <StatRow label="Balance owed" value={snap.owed} tone="amber" bold />
                </div>
              )}

              <label className="block">
                <div className="label-caps text-[9px] text-white/50 mb-1">Email on file</div>
                <input type="email" value={email}
                  onChange={(e) => setRecipients({ ...recipients, [`${p}_email`]: e.target.value })}
                  onBlur={saveRecipients}
                  data-testid={`rs-recipient-${p}`}
                  placeholder={`${p}@plenty-rydez.com`}
                  className="w-full bg-black/50 border border-white/15 rounded-lg px-3 py-2 text-sm text-white outline-none" />
              </label>

              <div className="flex gap-2">
                <button onClick={() => downloadPdf(p)} data-testid={`rs-download-${p}`}
                  className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg border border-cyan/40 text-cyan text-xs label-caps hover:bg-cyan/10 transition">
                  <FileText size={12} /> PDF
                </button>
                <button
                  disabled={!email || sending === p}
                  onClick={() => sendStatements([p], false)}
                  data-testid={`rs-send-${p}`}
                  className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg bg-cyan text-black text-xs font-bold label-caps disabled:opacity-40 transition">
                  {sending === p
                    ? <Loader2 size={12} className="animate-spin" />
                    : <><Mail size={12} /> Email</>}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="rounded-2xl border border-white/10 bg-black/25 p-4 space-y-2">
        <div className="label-caps text-[10px] text-cyan tracking-widest">Bulk actions</div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => sendStatements(["lance", "vaughn"], false)}
            disabled={sending === "lance,vaughn"}
            data-testid="rs-send-both"
            className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-cyan text-black text-xs font-bold label-caps disabled:opacity-40">
            {sending === "lance,vaughn"
              ? <Loader2 size={12} className="animate-spin" />
              : <><Send size={12} /> Email both partners now</>}
          </button>
          <div className="flex items-center gap-2 flex-1 min-w-[240px]">
            <input type="email" placeholder="Send TEST copy to…" value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              data-testid="rs-testto"
              className="flex-1 bg-black/50 border border-white/15 rounded-lg px-3 py-2 text-xs text-white outline-none" />
            <button onClick={() => sendStatements(["lance", "vaughn"], true)}
              disabled={!testTo || sending}
              data-testid="rs-send-test"
              className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-amber-400/40 text-amber-200 text-xs label-caps disabled:opacity-40">
              <Mail size={12} /> Test send
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatRow({ label, value, tone = "white", bold = false }) {
  const c = {
    white: "text-white/80", cyan: "text-cyan", lime: "text-lime-300",
    amber: "text-amber-300", red: "text-red-300",
  }[tone] || "text-white/80";
  return (
    <div className="rounded-lg bg-black/40 border border-white/10 px-3 py-2 flex justify-between items-center">
      <span className="label-caps text-[9px] text-white/50">{label}</span>
      <span className={`tabular-nums ${c} ${bold ? "font-bold" : ""}`}>
        ${Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
      </span>
    </div>
  );
}

// ── Deposit refund panel ─────────────────────────────────────────────
// Small compact form below the deposit-collection section. Shows only when
// a deposit was actually collected (`row.deposit_paid_amount > 0`) or when
// there's an existing refund on record (so managers can see the audit).
function DepositRefundPanel({ row, patch }) {
  const already = Number(row.deposit_refunded_amount || 0);
  const paid = Number(row.deposit_paid_amount || 0);
  const suggested = already > 0 ? already : paid;
  const [amount, setAmount] = useState(suggested);
  const [method, setMethod] = useState(row.deposit_refund_method || "");
  const [who, setWho] = useState(row.deposit_refunded_by || "");
  const [notes, setNotes] = useState("");
  const canSubmit = amount > 0 && !!method && !!who;
  const isRefunded = already > 0;

  const submit = async () => {
    if (!canSubmit) return;
    await patch({
      deposit_refunded_amount: Number(amount),
      deposit_refund_method: method,
      deposit_refunded_by: who,
      deposit_refund_notes: notes.trim() || undefined,
    });
    setNotes("");
  };
  const clear = async () => {
    await patch({ deposit_refunded_amount: 0 });
  };

  return (
    <div className={`rounded-xl border p-3 space-y-2 ${
        isRefunded ? "border-emerald-400/30 bg-emerald-400/[0.04]" : "border-white/10 bg-black/30"}`}
      data-testid="rs-refund-panel">
      <div className="flex items-center justify-between">
        <div className="label-caps text-[10px] text-cyan tracking-widest">Deposit refund</div>
        {isRefunded && (
          <button type="button" onClick={clear} data-testid="rs-refund-clear"
            className="text-[10px] label-caps text-white/50 hover:text-red-300">
            Clear refund
          </button>
        )}
      </div>

      {isRefunded ? (
        <div className="text-[11px] text-white/70">
          Refunded <span className="text-emerald-300 font-bold">${already.toFixed(0)}</span>
          {row.deposit_refund_method && (
            <> via <span className="text-white">{DEPOSIT_METHOD_LABEL[row.deposit_refund_method] || row.deposit_refund_method}</span></>
          )}
          {row.deposit_refunded_by && (
            <> by <span className="text-white">{COLLECTOR_LABEL[row.deposit_refunded_by] || row.deposit_refunded_by}</span></>
          )}
          {row.deposit_refunded_at && (
            <> on <span className="text-white/80">{new Date(row.deposit_refunded_at).toLocaleDateString()}</span></>
          )}
        </div>
      ) : (
        <div className="text-white/50 text-[11px]">
          Log the return date + method here. Setting an amount flips this deposit to
          <span className="text-emerald-300"> refunded</span> and drops it out of the held-deposit ledger.
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        <label className="w-full sm:w-32">
          <div className="label-caps text-[9px] text-white/50 mb-1">Amount</div>
          <div className="relative">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-white/40 text-sm">$</span>
            <input type="number" min="0" step="1"
              data-testid="rs-refund-amount"
              value={amount}
              onChange={(e) => setAmount(Number(e.target.value) || 0)}
              className="w-full bg-black/50 border border-white/15 rounded-lg pl-6 pr-2 py-2 text-sm text-white outline-none focus:border-emerald-400/50" />
          </div>
        </label>
        <label className="flex-1 min-w-[150px]">
          <div className="label-caps text-[9px] text-white/50 mb-1">Refunded via</div>
          <select
            data-testid="rs-refund-method"
            value={method}
            onChange={(e) => setMethod(e.target.value)}
            className="w-full bg-black/50 border border-white/15 rounded-lg px-3 py-2 text-sm text-white outline-none">
            <option value="">— select —</option>
            {DEPOSIT_METHOD_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </label>
        <label className="flex-1 min-w-[150px]">
          <div className="label-caps text-[9px] text-white/50 mb-1">Refunded by</div>
          <select
            data-testid="rs-refund-collector"
            value={who}
            onChange={(e) => setWho(e.target.value)}
            className="w-full bg-black/50 border border-white/15 rounded-lg px-3 py-2 text-sm text-white outline-none">
            <option value="">— select —</option>
            {COLLECTOR_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </label>
      </div>

      <div className="flex gap-2 items-center">
        <input
          data-testid="rs-refund-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Optional notes (reason / reference)"
          className="flex-1 bg-black/50 border border-white/15 rounded-lg px-3 py-2 text-sm text-white outline-none" />
        <button type="button"
          data-testid="rs-refund-submit"
          disabled={!canSubmit}
          onClick={submit}
          className="px-4 py-2 rounded-lg bg-emerald-400 text-black text-xs font-bold label-caps disabled:opacity-40">
          {isRefunded ? "Update refund" : "Record refund"}
        </button>
      </div>
    </div>
  );
}
